import { ChartBarIcon } from "@heroicons/react/outline";
import { TableIcon } from "lucide-react";
import { useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useLocation, useParams } from "react-router-dom";

import AddTraceToDatasetButton from "./AddTraceToDatasetButton";
import TraceSummaryPills from "./TraceSummaryPills";
import { getTraceTableLink } from "./utils/getTraceTableLink";

import { RetentionPolicyBadge } from "@/components/ContentNavbar/HistoryPageNavbar/RetentionPolicyBadge";
import { LinkWithSearch } from "@/components/LinkWithSearch";
import { FilterValueType } from "@/components/StructuredSearch/config/structured-search-schema";
import type { Filter } from "@/components/StructuredSearch/config/types";
import { applyStructuredSearchStateToSearchParams } from "@/components/StructuredSearch/hooks/useStructuredSearchURLSync";
import {
  type CompleteDateRange,
  createStructuredSearchDefaultDateRange,
} from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import { SESSION_STORAGE } from "@/constants";
import { useUser } from "@/context/user-context";
import type { SpanNode } from "@/types/spans";
import { readDateRangeFromSearchParams } from "@/utils/utils";

interface TraceDetailHeaderProps {
  traceId?: string;
  traceName?: string | null;
  selectedSpan: SpanNode | null;
  spanTree?: SpanNode[] | null;
  loggedBeforeRetentionPolicy?: boolean;
  flattenedSpans: SpanNode[];
  setSelectedSpan: Dispatch<SetStateAction<SpanNode | null>>;
}

const getTraceAnalyticsDateRange = ({
  baseDateRange,
  tracesSearchCutoverDate,
}: {
  baseDateRange: CompleteDateRange;
  tracesSearchCutoverDate: string | null | undefined;
}): CompleteDateRange => {
  const cutoverDate = tracesSearchCutoverDate ? new Date(tracesSearchCutoverDate) : null;
  if (!cutoverDate || !Number.isFinite(cutoverDate.getTime())) {
    return baseDateRange;
  }

  if (baseDateRange.from.getTime() >= cutoverDate.getTime()) {
    return baseDateRange;
  }

  const to = baseDateRange.to.getTime() < cutoverDate.getTime() ? new Date() : baseDateRange.to;

  return {
    from: cutoverDate,
    to,
  };
};

const buildTraceNameAnalyticsHref = ({
  workspaceId,
  baseSearch,
  traceName,
  tracesSearchCutoverDate,
}: {
  workspaceId: string | undefined;
  baseSearch: string;
  traceName: string | null | undefined;
  tracesSearchCutoverDate: string | null | undefined;
}): string | null => {
  const trimmedTraceName = traceName?.trim();
  if (!workspaceId || !trimmedTraceName) {
    return null;
  }

  const currentParams = new URLSearchParams(baseSearch);
  const { startTime, endTime } = readDateRangeFromSearchParams(currentParams, [
    "traces",
    "default",
  ]);
  const hasValidDateRange =
    startTime &&
    endTime &&
    Number.isFinite(startTime.getTime()) &&
    Number.isFinite(endTime.getTime());
  const dateRange = hasValidDateRange
    ? { from: startTime, to: endTime }
    : createStructuredSearchDefaultDateRange();
  const analyticsDateRange = getTraceAnalyticsDateRange({
    baseDateRange: dateRange,
    tracesSearchCutoverDate,
  });
  const filters: Filter[] = [
    {
      id: crypto.randomUUID(),
      field: "trace_name",
      type: FilterValueType.String,
      operator: "is",
      value: trimmedTraceName,
    },
  ];
  const params = applyStructuredSearchStateToSearchParams(
    currentParams,
    {
      filters,
      dateRange: analyticsDateRange,
      q: "",
      sorting: [],
    },
    { resetPage: true, namespace: "tracesTable" },
  );
  const qs = params.toString();

  return `/workspace/${workspaceId}/traces-analytics${qs ? `?${qs}` : ""}`;
};

const TraceDetailHeader = ({
  traceId,
  traceName,
  selectedSpan,
  spanTree,
  loggedBeforeRetentionPolicy,
  flattenedSpans,
  setSelectedSpan,
}: TraceDetailHeaderProps) => {
  const { workspaceId } = useParams();
  const { activeWorkspace } = useUser();
  const location = useLocation();
  const analyticsHref = useMemo(
    () =>
      buildTraceNameAnalyticsHref({
        workspaceId,
        baseSearch: location.search,
        traceName,
        tracesSearchCutoverDate: activeWorkspace?.traces_search_cutover_date,
      }),
    [activeWorkspace?.traces_search_cutover_date, location.search, traceName, workspaceId],
  );
  const tableHref = useMemo(
    () => getTraceTableLink(workspaceId, spanTree),
    [spanTree, workspaceId],
  );
  const storeReturnUrl = () => {
    sessionStorage.setItem(
      SESSION_STORAGE.TRACES_RETURN_URL,
      `${location.pathname}${location.search}${location.hash}`,
    );
  };

  return (
    <div className="flex flex-col items-start gap-3 [@media(min-width:1281px)]:flex-row [@media(min-width:1281px)]:items-center [@media(min-width:1281px)]:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-3">
          <h1 className="shrink-0 whitespace-nowrap text-2xl leading-tight text-gray-900">
            Trace Spans
          </h1>
          <TraceSummaryPills
            traceId={traceId}
            flattenedSpans={flattenedSpans}
            setSelectedSpan={setSelectedSpan}
          />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <p className="text-sm leading-5 text-gray-600">View and analyze trace spans</p>
          <RetentionPolicyBadge loggedBeforeRetentionPolicy={loggedBeforeRetentionPolicy} />
        </div>
      </div>
      <div className="flex flex-col items-start gap-2 [@media(min-width:1281px)]:items-end">
        <div className="flex flex-wrap items-center gap-2">
          {tableHref ? (
            <LinkWithSearch
              path={tableHref}
              className="inline-flex shrink-0 items-center gap-1 text-sm hover:text-gray-500"
            >
              <TableIcon className="h-4 w-4" />
              Open Table
            </LinkWithSearch>
          ) : null}
          {analyticsHref ? (
            <LinkWithSearch
              path={analyticsHref}
              onClick={storeReturnUrl}
              className="inline-flex shrink-0 items-center gap-1 text-sm hover:text-gray-500"
            >
              <ChartBarIcon className="h-4 w-4" />
              Analyze Similar Traces
            </LinkWithSearch>
          ) : null}
          <AddTraceToDatasetButton traceId={traceId} selectedSpan={selectedSpan} />
        </div>
        <span className="text-sm leading-5 text-gray-500">Trace ID: {traceId}</span>
      </div>
    </div>
  );
};

export default TraceDetailHeader;
